import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import type { MouseEvent } from 'react';
import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import LimitReachedTooltip from '~/components/isolated/LimitReachedTooltip';
import { useGlobal } from '~/hooks/useGlobal';

import styles from './CartCounter.module.scss';

export type CartCounterSize = 'm' | 's' | 'xs';
export type CounterAction = 'add' | 'remove';

export interface CartCounterProps {
  /**
   * Current count value
   */
  count: number;

  /**
   * Click handler for increment/decrement
   */
  onClick: (
    newCount: number,
    action: CounterAction,
    e: MouseEvent<HTMLButtonElement>,
  ) => void;

  /**
   * Maximum allowed quantity
   */
  limit: number;

  /**
   * Size variant (m, s)
   */
  size: CartCounterSize;

  /**
   * Additional CSS class
   */
  className?: string;

  /**
   * Transparent state - card becomes transparent
   */
  transparent?: boolean;
}

/**
 * CartCounter - Horizontal counter bar for cart items
 * Layout: [Minus] [Count] [Plus]
 */
const CartCounter = observer(
  ({
    count,
    onClick,
    limit,
    size,
    transparent = false,
    className,
  }: CartCounterProps) => {
    const { isMobile } = useGlobal();

    const isDecrementDisabled = count <= 0;
    const isIncrementDisabled = count >= limit;

    const iconSize = isMobile
      ? size === 's'
        ? 10
        : size === 'xs'
          ? 16
          : 24
      : size === 'xs'
        ? 20
        : 24;
    const showTooltip = size === 'm';

    const handleDecrement = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onClick(count - 1, 'remove', e);
    };

    const handleIncrement = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onClick(count + 1, 'add', e);
    };

    return (
      <div className={cn(styles.wrapper, className)}>
        {showTooltip && (
          <LimitReachedTooltip isLimitReached={isIncrementDisabled} />
        )}
        <div className={cn(styles.counter, styles[`counter_${size}`])}>
          <button
            type="button"
            onClick={handleDecrement}
            disabled={isDecrementDisabled}
            className={cn(
              styles.button,
              styles.buttonMinus,
              styles[`button_${size}`],
            )}
            aria-label="Decrease quantity"
          >
            <Icon
              type="minus-3.1"
              width={iconSize}
              className={cn(
                company.match({
                  mcosmetic: transparent && 'text-graphic-regular-brand!',
                }),
              )}
            />
          </button>

          <span className={cn(styles.count, styles[`count_${size}`])}>
            {count}
          </span>

          <button
            type="button"
            onClick={handleIncrement}
            disabled={isIncrementDisabled}
            className={cn(
              styles.button,
              styles.buttonPlus,
              styles[`button_${size}`],
            )}
            aria-label="Increase quantity"
          >
            <Icon
              type="plus-3.1"
              size={iconSize}
              className={cn(
                company.match({
                  mcosmetic: transparent && 'text-graphic-regular-brand!',
                }),
              )}
            />
          </button>
        </div>
      </div>
    );
  },
);

export default CartCounter;
